import AudioPlayer, { AudioPlayerProvider } from "@/components/AudioPlayer";
import CallLogs from "@/components/calls/CallLogs";
import CallTranscript from "@/components/calls/CallTranscript";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { CallResponse, PostCallWorkflowOutputs } from "@/lib/types";
import { useAuthStore } from "@/stores/authStore";
import {
  format,
  formatDistanceToNow,
  isToday,
  isYesterday,
  isThisWeek,
} from "date-fns";
import { parseApiTimestamp } from "@/lib/datetime";
import {
  Copy,
  ChevronDown,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

// Define new interfaces to match the updated log structure
interface TTFBLogEntry {
  date_time: string;
  service_name: string;
  ttfb: number;
}

interface EventLogEntry {
  date_time: string;
  event: string;
}

interface NewCallLog {
  ttfb: TTFBLogEntry[];
  events: EventLogEntry[];
}

interface NavigationState {
  callIds?: string[];
  currentIndex?: number;
  filters?: any;
  totalCalls?: number;
  returnUrl?: string;
  sorting?: any;
  currentPage?: number;
  pageSize?: number;
}

function CallPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [call, setCall] = useState<CallResponse | null>(null);
  const [callLogs, setCallLogs] = useState<NewCallLog | null>(null);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const [transcript, setTranscript] = useState<string>("");
  const [workflowOutputs, setWorkflowOutputs] =
    useState<PostCallWorkflowOutputs | null>(null);
  const [loadingRecording, setLoadingRecording] = useState(false);
  const [recordingUnavailable, setRecordingUnavailable] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [loadingWorkflowOutputs, setLoadingWorkflowOutputs] = useState(false);
  const [isCallInfoOpen, setIsCallInfoOpen] = useState(true);
  const [isCallLogsOpen, setIsCallLogsOpen] = useState(false);
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(true);
  const [navigationLoading, setNavigationLoading] = useState(false);
  const { user } = useAuthStore();

  // Get navigation state from location
  const navigationState = location.state as NavigationState | null;

  // Navigation helpers
  const hasPrevious =
    navigationState?.currentIndex !== undefined &&
    navigationState.currentIndex > 0;
  const hasNext =
    navigationState?.currentIndex !== undefined &&
    navigationState.callIds &&
    navigationState.currentIndex < navigationState.callIds.length - 1;

  useEffect(() => {
    if (id) {
      setNavigationLoading(true);
      // Load call details first to show the page immediately
      fetchCallDetails(id).finally(() => {
        setNavigationLoading(false);
      });

      // Load other data independently
      fetchCallRecording(id);
      if (user?.is_superuser) {
        fetchCallLogs(id);
      }
      fetchCallTranscript(id);
      fetchWorkflowOutputs(id);
    }
  }, [id, user]);

  const fetchCallTranscript = async (callId: string) => {
    try {
      setLoadingTranscript(true);
      const transcriptData = await api.getCallTranscript(callId);
      setTranscript(transcriptData);
    } catch (error) {
      console.error("Failed to fetch call transcript:", error);
      toast({
        title: "Error",
        description: "Failed to fetch call transcript. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingTranscript(false);
    }
  };

  const fetchWorkflowOutputs = async (callId: string) => {
    try {
      setLoadingWorkflowOutputs(true);
      const outputs = await api.getPostCallWorkflowOutputs(callId);
      setWorkflowOutputs(outputs);
    } catch (error) {
      console.error("Failed to fetch workflow outputs:", error);
      toast({
        title: "Error",
        description: "Failed to fetch workflow outputs. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingWorkflowOutputs(false);
    }
  };

  const fetchCallLogs = async (callId: string) => {
    try {
      setLoadingLogs(true);
      const logs = await api.getCallLog(callId);
      setCallLogs(logs.log_lines as unknown as NewCallLog);
    } catch (error) {
      console.error("Failed to fetch call logs:", error);
      toast({
        title: "Error",
        description: "Failed to fetch call logs. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchCallDetails = async (callId: string) => {
    try {
      const details = await api.getCall(callId);
      setCall(details);
    } catch (error) {
      console.error("Failed to fetch call details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch call details. Please try again.",
        variant: "destructive",
      });
    }
  };

  const fetchCallRecording = async (callId: string) => {
    setRecordingBlob(null);
    setRecordingUnavailable(false);
    try {
      setLoadingRecording(true);
      const recording = await api.getCallRecording(callId);
      setRecordingBlob(recording);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        // No recording exists for this call: show an inline state, not a toast.
        setRecordingUnavailable(true);
      } else {
        console.error("Failed to fetch call recording:", error);
        toast({
          title: "Error",
          description: "Failed to fetch call recording. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setLoadingRecording(false);
    }
  };

  const copyCallId = async () => {
    if (call?.id) {
      await navigator.clipboard.writeText(call.id);
      toast({
        title: "Success",
        description: "Call ID copied to clipboard",
      });
    }
  };

  const handlePreviousCall = () => {
    if (navigationState?.callIds && hasPrevious) {
      const previousCallId =
        navigationState.callIds[navigationState.currentIndex! - 1];
      navigate(`/calls/${previousCallId}`, {
        state: {
          ...navigationState,
          currentIndex: navigationState.currentIndex! - 1,
        },
      });
    }
  };

  const handleNextCall = () => {
    if (navigationState?.callIds && hasNext) {
      const nextCallId =
        navigationState.callIds[navigationState.currentIndex! + 1];
      navigate(`/calls/${nextCallId}`, {
        state: {
          ...navigationState,
          currentIndex: navigationState.currentIndex! + 1,
        },
      });
    }
  };

  const handleBackToCalls = () => {
    if (navigationState?.returnUrl) {
      navigate(navigationState.returnUrl);
    } else {
      navigate("/calls");
    }
  };

  const formatCallDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return "-";

    try {
      // API timestamps without an offset are UTC; shown in local time.
      const date = parseApiTimestamp(dateString);
      if (!date) return dateString;

      // Format the main date display
      let formattedDate;
      if (isToday(date)) {
        formattedDate = `Today at ${format(date, "h:mm a")}`;
      } else if (isYesterday(date)) {
        formattedDate = `Yesterday at ${format(date, "h:mm a")}`;
      } else if (isThisWeek(date)) {
        formattedDate = format(date, "EEEE h:mm a");
      } else {
        formattedDate = format(date, "MM/dd/yy h:mm a");
      }

      // Add relative time
      const timeAgo = formatDistanceToNow(date, { addSuffix: true });

      return (
        <div className="flex flex-wrap items-baseline gap-x-2">
          <div>{formattedDate}</div>
          <div className="text-sm text-muted-foreground">{timeAgo}</div>
        </div>
      );
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  if (navigationLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="ml-2">Loading call details...</p>
      </div>
    );
  }

  if (!call) {
    return <div>Loading...</div>;
  }

  return (
    <div className="mx-auto w-full max-w-6xl min-w-0 px-4 py-4 md:px-6 md:py-6">
      {/* Header with navigation */}
      <div className="mb-4 flex flex-col gap-3 md:mb-6 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-3 md:gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackToCalls}
            className="flex items-center gap-2 md:h-10 md:px-4"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to Calls</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <h1 className="text-2xl font-bold md:text-3xl">Call Details</h1>
        </div>

        {/* Navigation controls */}
        {navigationState && (
          <div className="flex items-center justify-between gap-2 md:justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousCall}
              disabled={!hasPrevious || navigationLoading}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            {navigationState.currentIndex !== undefined &&
              navigationState.callIds && (
                <span className="text-sm text-muted-foreground px-2 md:px-3 whitespace-nowrap">
                  {navigationState.currentIndex + 1} of{" "}
                  {navigationState.callIds.length}
                </span>
              )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextCall}
              disabled={!hasNext || navigationLoading}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      {call && (
        <div className="flex min-w-0 flex-col gap-4 md:m-8">
          <Card className="w-full min-w-0 mt-2 md:mt-4">
            <CardHeader>
              <CardTitle>Call Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2 md:text-base">
                <div className="flex min-w-0 items-center gap-2">
                  <strong className="shrink-0">Call ID:</strong>
                  <span className="min-w-0 break-all">{call.id}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={copyCallId}
                    className="h-8 w-8 shrink-0"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <div className="break-words">
                  <strong>Callee Phone Number:</strong>{" "}
                  {call.callee_phone_number}
                </div>
                <div className="break-words">
                  <strong>Status:</strong> {call.call_status}
                </div>
                <div className="break-words">
                  <strong>Strategy Name:</strong> {call.strategy_name}
                </div>
                <div className="flex flex-wrap items-baseline gap-x-1">
                  <strong>Started At:</strong>
                  <div>{formatCallDateTime(call.call_started_at)}</div>
                </div>
                <div className="break-words">
                  <strong>Strategy Version:</strong>{" "}
                  {call.strategy_version.version_description}
                </div>
                <div className="flex flex-wrap items-baseline gap-x-1">
                  <strong>Ended At:</strong>
                  <div>{formatCallDateTime(call.call_ended_at)}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Call Specific Info Card */}
          {call.call_specific_info &&
            Object.keys(call.call_specific_info).length > 0 && (
              <Collapsible
                open={isCallInfoOpen}
                onOpenChange={setIsCallInfoOpen}
                className="w-full mt-4"
              >
                <Card className="w-full">
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="hover:bg-muted/50 cursor-pointer">
                      <CardTitle className="flex items-center justify-between">
                        <span>Call Specific Information</span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${
                            isCallInfoOpen ? "transform rotate-180" : ""
                          }`}
                        />
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries(call.call_specific_info).map(
                          ([key, value]) => (
                            <div
                              key={key}
                              className="border-b border-border pb-2 last:border-b-0"
                            >
                              <div className="font-semibold text-sm text-muted-foreground mb-1 capitalize">
                                {key.replace(/_/g, " ")}
                              </div>
                              <div className="text-sm min-w-0">
                                {typeof value === "string" ? (
                                  <div className="whitespace-pre-wrap break-words text-sm">
                                    {value}
                                  </div>
                                ) : (
                                  <pre className="whitespace-pre-wrap break-words font-mono text-xs bg-muted/50 p-2 rounded overflow-x-auto">
                                    {JSON.stringify(value, null, 2)}
                                  </pre>
                                )}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

          {/* Recording Card: always rendered with loading / player / no-recording states */}
          <Card className="w-full min-w-0 mt-4">
            <CardHeader>
              <CardTitle>Call Recording</CardTitle>
            </CardHeader>
            <CardContent className="min-w-0">
              {loadingRecording ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading recording…
                </div>
              ) : recordingBlob ? (
                <AudioPlayerProvider>
                  <AudioPlayer
                    id={call.id}
                    audioBlob={recordingBlob}
                    darkMode={false}
                    height={100}
                    layoutType="row"
                  />
                </AudioPlayerProvider>
              ) : recordingUnavailable ? (
                <div className="text-sm text-muted-foreground">
                  No recording available for this call
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Recording could not be loaded
                </div>
              )}
            </CardContent>
          </Card>

          {user?.is_superuser && (
            <Collapsible
              open={isCallLogsOpen}
              onOpenChange={setIsCallLogsOpen}
              className="w-full mt-4"
            >
              <Card className="w-full">
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="hover:bg-muted/50 cursor-pointer">
                    <CardTitle className="flex items-center justify-between">
                      <span>Call Logs</span>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${
                          isCallLogsOpen ? "transform rotate-180" : ""
                        }`}
                      />
                    </CardTitle>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="min-w-0">
                    <CallLogs callLogs={callLogs} loading={loadingLogs} />
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}

          {/* Transcript Card */}
          <Collapsible
            open={isTranscriptOpen}
            onOpenChange={setIsTranscriptOpen}
            className="w-full mt-4"
          >
            <Card className="w-full">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="hover:bg-muted/50 cursor-pointer">
                  <CardTitle className="flex items-center justify-between">
                    <span>Call Transcript</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-200 ${
                        isTranscriptOpen ? "transform rotate-180" : ""
                      }`}
                    />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  {loadingTranscript ? (
                    <div className="text-muted-foreground font-bold">
                      Loading transcript...
                    </div>
                  ) : transcript ? (
                    <ScrollArea className="h-[60dvh] md:h-[500px] w-full rounded-md border">
                      {transcript.trimStart().startsWith("WEBVTT") ? (
                        <CallTranscript transcriptText={transcript} />
                      ) : (
                        <div className="p-4 whitespace-pre-wrap break-words text-sm md:text-base">
                          {transcript}
                        </div>
                      )}
                    </ScrollArea>
                  ) : (
                    <div className="text-muted-foreground">No transcript available</div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Workflow Outputs Card */}
          <Card className="w-full mt-4">
            <CardHeader>
              <CardTitle>Post-Call Workflow Outputs</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingWorkflowOutputs ? (
                <div className="text-muted-foreground font-bold">
                  Loading workflow outputs...
                </div>
              ) : workflowOutputs && Object.keys(workflowOutputs).length > 0 ? (
                <ScrollArea className="h-[60dvh] md:h-[400px] w-full rounded-md border">
                  <div className="p-4 pr-12">
                    <pre className="whitespace-pre-wrap break-words text-xs md:text-sm overflow-x-auto">
                      {JSON.stringify(workflowOutputs, null, 2)}
                    </pre>
                  </div>
                  <div className="absolute top-2 right-2 md:top-4 md:right-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          JSON.stringify(workflowOutputs, null, 2)
                        );
                        toast({
                          title: "Success",
                          description: "Workflow outputs copied to clipboard",
                        });
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-muted-foreground">
                  No workflow outputs available
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default CallPage;
